import React from "react";
import type { GetRef } from "antd";

import { InboxOutlined } from "@ant-design/icons";
import { App, Typography, Upload, UploadProps } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import request from "../../api/request/http-service";
import { DOC_API_CONTROLLER_END_POINTS } from "../../config/apiEndPoints/DocApiEndPoints";
import { SERVER_ADDRESSES } from "../../config/serverAddressesConfig";
import { makeSelectDocumentTypes, makeSelectHost } from "../../containers/App/selectors";
import DocumentModel from "../../viewModels/Documents/DocumentModel";

type Props = UploadProps<DocumentModel> & {
  content?: React.ReactNode;
  onAttachFiles?: (files: Array<DocumentModel>) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
};

const UploadFileDragger = React.memo(
  React.forwardRef<GetRef<typeof Upload.Dragger<DocumentModel>>, Props>(function UploadFileDragger(
    { content, onAttachFiles, onUploadStart, onUploadEnd, ...props },
    ref,
  ) {
    const { t } = useTranslation("translations");
    const { message } = App.useApp();
    const documentTypes = useSelector(makeSelectDocumentTypes());
    const msdsDocumentType = documentTypes.find((documentType) => documentType.type === "MSDS")!;
    const host = useSelector(makeSelectHost());
    const [pendingCount, setPendingCount] = React.useState(0);
    const uploadedDocuments = React.useRef<Array<DocumentModel>>([]);

    React.useEffect(() => {
      if (pendingCount === 0 && uploadedDocuments.current.length > 0 && onAttachFiles) {
        const documents = uploadedDocuments.current;
        uploadedDocuments.current = [];
        onAttachFiles(documents);
      }
    }, [pendingCount, onAttachFiles]);

    return (
      <Upload.Dragger
        {...props}
        ref={ref}
        listType="text"
        customRequest={async ({ file, onError, onSuccess }) => {
          setPendingCount((count) => count + 1);
          onUploadStart?.();
          try {
            const response = (await request(
              {
                server: SERVER_ADDRESSES.DOC_API,
                controller: DOC_API_CONTROLLER_END_POINTS.UPLOAD_CLIENT_PATH,
                requestMethod: "post",
                headers: { "content-type": "multipart/form-data" },
                body: { file },
                query: { name: (file as File).name, typeId: msdsDocumentType.id },
              },
              host,
            )) as DocumentModel;
            onSuccess?.(response);
            if (onAttachFiles) {
              uploadedDocuments.current.push(response);
            }
          } catch (_error) {
            message.error(t("unable_upload_file_max_size"));
            onError?.(new Error(t("unable_upload_file_max_size")));
          } finally {
            setPendingCount((count) => count - 1);
            onUploadEnd?.();
          }
        }}
      >
        {content || (
          <>
            <InboxOutlined style={{ fontSize: 20 }} />
            <div>
              <Typography.Text strong>{t("click_or_drag_files")}</Typography.Text>
            </div>
          </>
        )}
      </Upload.Dragger>
    );
  }),
);

export default UploadFileDragger;
